import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminAnalyticsPage } from '@/components/admin/analytics-page';
import {
    ANALYTICS_MAX_RANGE_DAYS,
    inclusiveDayCount,
} from '@/lib/analytics-series';
import { adminHead } from '@/lib/seo';
import { adminAnalyticsPageFn } from '@/server/functions/analytics';

const dayKeySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/);

const searchSchema = z.object({
    days: z
        .union([z.number(), z.string()])
        .optional()
        .transform((value) => {
            if (value == null || value === '') return undefined;
            const n = typeof value === 'number' ? value : Number(value);
            if (
                !Number.isInteger(n) ||
                n < 1 ||
                n > ANALYTICS_MAX_RANGE_DAYS
            ) {
                return undefined;
            }
            return n;
        }),
    start: z.string().optional(),
    end: z.string().optional(),
}).transform((value) => {
    const startParsed = dayKeySchema.safeParse(value.start);
    const endParsed = dayKeySchema.safeParse(value.end);
    if (startParsed.success && endParsed.success) {
        const start =
            startParsed.data <= endParsed.data
                ? startParsed.data
                : endParsed.data;
        const end =
            startParsed.data <= endParsed.data
                ? endParsed.data
                : startParsed.data;
        const span = inclusiveDayCount(start, end);
        if (span >= 1 && span <= ANALYTICS_MAX_RANGE_DAYS) {
            return { days: undefined, start, end };
        }
    }
    return {
        days: value.days ?? 30,
        start: undefined,
        end: undefined,
    };
});

export const Route = createFileRoute('/_admin/admin/analytics')({
    component: AnalyticsRoute,
    head: () => adminHead('אנליטיקס', '/admin/analytics'),
    validateSearch: searchSchema,
    staleTime: 0,
    preloadStaleTime: 0,
    loaderDeps: ({ search }) => ({
        days: search.days,
        start: search.start,
        end: search.end,
    }),
    loader: ({ deps }) => {
        if (deps.start && deps.end) {
            return adminAnalyticsPageFn({
                data: { start: deps.start, end: deps.end },
            });
        }
        return adminAnalyticsPageFn({
            data: { days: deps.days ?? 30 },
        });
    },
});

function AnalyticsRoute() {
    const data = Route.useLoaderData();
    const navigate = useNavigate({ from: Route.fullPath });

    return (
        <AdminAnalyticsPage
            data={data}
            onRangeChange={(range) => {
                if (range.days) {
                    void navigate({
                        search: { days: range.days },
                    });
                    return;
                }
                void navigate({
                    search: { start: range.start, end: range.end },
                });
            }}
        />
    );
}
